/**
 * IndexedDB wrapper for AccouPro offline storage.
 * Stores offline entries with metadata for sync.
 */

export type OfflineEntryType = "invoice" | "voucher" | "expense" | "customer" | "inventory";
export type SyncStatus = "pending" | "synced" | "failed" | "conflict";

export interface OfflineEntry {
  id: string;
  type: OfflineEntryType;
  data: Record<string, unknown>;
  status: SyncStatus;
  createdAt: string;
  updatedAt: string;
  syncAttempts: number;
  lastSyncError?: string;
  conflictData?: {
    localVersion: Record<string, unknown>;
    cloudVersion: Record<string, unknown>;
  };
}

const DB_NAME = "accoupro-offline";
const DB_VERSION = 1;
const STORE_NAME = "entries";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("status", "status", { unique: false });
        store.createIndex("type", "type", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function addOfflineEntry(entry: Omit<OfflineEntry, "id" | "createdAt" | "updatedAt" | "syncAttempts" | "status">): Promise<OfflineEntry> {
  const db = await openDB();
  const now = new Date().toISOString();
  const record: OfflineEntry = {
    ...entry,
    id: `offline-${crypto.randomUUID().slice(0, 12)}`,
    status: "pending",
    createdAt: now,
    updatedAt: now,
    syncAttempts: 0,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => resolve(record);
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllEntries(): Promise<OfflineEntry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getEntriesByStatus(status: SyncStatus): Promise<OfflineEntry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const idx = tx.objectStore(STORE_NAME).index("status");
    const req = idx.getAll(status);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function updateEntry(id: string, updates: Partial<OfflineEntry>): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      if (getReq.result) {
        store.put({ ...getReq.result, ...updates, updatedAt: new Date().toISOString() });
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteEntry(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearSyncedEntries(): Promise<number> {
  const synced = await getEntriesByStatus("synced");
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    synced.forEach((e) => store.delete(e.id));
    tx.oncomplete = () => resolve(synced.length);
    tx.onerror = () => reject(tx.error);
  });
}

export async function getStats(): Promise<{ pending: number; synced: number; failed: number; conflict: number; total: number }> {
  const all = await getAllEntries();
  return {
    pending: all.filter((e) => e.status === "pending").length,
    synced: all.filter((e) => e.status === "synced").length,
    failed: all.filter((e) => e.status === "failed").length,
    conflict: all.filter((e) => e.status === "conflict").length,
    total: all.length,
  };
}
